import { el } from '../../core/dom.js';
import { toast } from '../../components/toast.js';
import {
  kbFileAllowed, KB_MAX_BYTES, renderDropZone, uploadDocument, readBadge,
} from '../knowledge/upload.js';

/**
 * Map saved documents into the same job shape Knowledge uses, so badges match.
 * @param {object[]} docs
 * @param {object[]} jobs
 */
export function jobsFromDocs(docs, jobs) {
  const pending = (jobs || []).filter((job) => job.phase !== 'done');
  const done = (docs || []).map((doc) => ({
    key: `doc-${doc.id}`,
    name: doc.title || doc.filename || 'File',
    phase: 'done',
    pct: 100,
    error: '',
    doc,
    file: null,
  }));
  return [...pending, ...done];
}

/**
 * Step 2 — context files. Reuses Knowledge drop zone + upload progress UX.
 * @param {object} state
 * @param {{
 *   onContinue: (action: string) => void,
 *   onJobs: () => void,
 * }} handlers
 */
export function renderFiles(state, handlers) {
  const jobs = jobsFromDocs(state.files, state.jobs);
  return el('div', { class: 'np-files' },
    el('div', { class: 'np-files-main' },
      renderDropZone({
        jobs,
        onFiles: (list) => queueFiles(state, list, handlers),
        onRetry: (key) => retryJob(state, key, handlers),
      })),
    el('aside', { class: 'np-files-side' },
      el('div', { class: 'np-glass np-why' },
        el('div', { class: 'np-why-title' }, 'Why this matters'),
        el('p', {}, 'Analytics tells Bonnie ', el('em', {}, 'what'), ' users did. These files tell her what you ', el('em', {}, 'intended'), ' — that\'s how a number becomes a recommendation.')),
      el('div', { class: 'np-actions np-actions-col' },
        el('button', {
          type: 'button',
          class: 'np-cta',
          onClick: () => handlers.onContinue('continue_files'),
        }, 'Continue to connectors ', el('span', { class: 'arw', 'aria-hidden': 'true' }, '→')),
        el('button', {
          type: 'button',
          class: 'np-btn',
          onClick: () => handlers.onContinue('skip_files'),
        }, 'Add files later'))));
}

function queueFiles(state, list, handlers) {
  if (!state.productId) {
    toast('Save the product first', 'error');
    return;
  }
  const files = [...(list || [])];
  const skipped = files.filter((file) => !kbFileAllowed(file));
  if (skipped.length) toast('Audio and video are skipped. Upload documents or images.', 'info');
  files.filter(kbFileAllowed).forEach((file, index) => {
    const tooBig = file.size > KB_MAX_BYTES;
    state.jobs.push({
      key: `up-${Date.now()}-${index}-${file.name}`,
      name: file.name,
      file,
      phase: tooBig ? 'error' : 'queued',
      pct: 0,
      error: tooBig ? 'File too large (max 40 MB)' : '',
      doc: null,
    });
  });
  handlers.onJobs();
  pump(state, handlers);
}

function retryJob(state, key, handlers) {
  const job = state.jobs.find((item) => item.key === key);
  if (!job) return;
  job.phase = 'queued';
  job.error = '';
  job.pct = 0;
  handlers.onJobs();
  pump(state, handlers);
}

async function pump(state, handlers) {
  if (state.pumping) return;
  state.pumping = true;
  try {
    let next = state.jobs.find((job) => job.phase === 'queued');
    while (next) {
      await runJob(state, next, handlers);
      next = state.jobs.find((job) => job.phase === 'queued');
    }
  } finally {
    state.pumping = false;
  }
}

async function runJob(state, job, handlers) {
  job.phase = 'upload';
  job.pct = 0;
  handlers.onJobs();
  const result = await uploadDocument(job.file, state.productId, {
    onProgress(pct) {
      job.phase = 'upload';
      job.pct = pct;
      handlers.onJobs();
    },
    onIngest() {
      job.phase = 'ingest';
      handlers.onJobs();
    },
  });
  if (!result || result.error || !(result.document || result.id)) {
    job.phase = 'error';
    job.error = (result && result.error) || 'Upload failed';
    handlers.onJobs();
    toast(`Could not upload ${job.name}: ${job.error}`, 'error');
    return;
  }
  job.phase = 'done';
  job.doc = result.document || result;
  const known = (state.files || []).some((doc) => doc.id === job.doc.id);
  state.files = known
    ? state.files.map((doc) => (doc.id === job.doc.id ? job.doc : doc))
    : [job.doc, ...(state.files || [])];
  state.jobs = state.jobs.filter((item) => item.key !== job.key);
  handlers.onJobs();
}

/** Exported for tests that assert badge labels stay aligned with Knowledge. */
export { readBadge };
